import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoredUser, logout } from '@/hooks/useAuth';
import {
  WizardData,
  defaultWizardData,
} from '@/mocks/wizard';
import WizardStepper from './components/WizardStepper';
import StepProfile from './components/StepProfile';
import StepCompany from './components/StepCompany';
import StepModules from './components/StepModules';
import StepIntegrations from './components/StepIntegrations';
import StepTeam from './components/StepTeam';

const WIZARD_DATA_KEY = (userId: string) => `helios_wizard_data_${userId}`;
const WIZARD_DONE_KEY = (userId: string) => `helios_setup_${userId}`;

const STEPS = [
  { index: 1, label: 'Профіль' },
  { index: 2, label: 'Компанія' },
  { index: 3, label: 'Модулі' },
  { index: 4, label: 'Інтеграції' },
  { index: 5, label: 'Команда' },
];

export default function SetupWizardPage() {
  const navigate = useNavigate();
  const user = getStoredUser();

  const [step, setStep] = useState(1);
  const [data, setData] = useState<WizardData>(() => {
    if (!user) return defaultWizardData;
    try {
      const raw = localStorage.getItem(WIZARD_DATA_KEY(user.id));
      if (raw) return JSON.parse(raw) as WizardData;
    } catch {
      // ignore
    }
    return {
      ...defaultWizardData,
      profile: {
        ...defaultWizardData.profile,
        firstName: user.displayName.split(' ')[0] ?? '',
        lastName: user.displayName.split(' ').slice(1).join(' ') ?? '',
        position: user.role,
      },
    };
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user) {
      localStorage.setItem(WIZARD_DATA_KEY(user.id), JSON.stringify(data));
    }
  }, [data, user]);

  const goNext = () => {
    if (step < 5) {
      setStep((s) => s + 1);
    } else {
      handleComplete();
    }
  };

  const goBack = () => {
    if (step > 1) setStep((s) => s - 1);
  };

  const handleComplete = () => {
    if (!user) return;
    localStorage.setItem(WIZARD_DONE_KEY(user.id), 'done');
    navigate('/');
  };

  const handleSkip = () => {
    if (!user) return;
    localStorage.setItem(WIZARD_DONE_KEY(user.id), 'done');
    navigate('/');
  };

  if (!user) return null;

  const selectedModulesCount = data.selectedModules.length;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F5F5F7', fontFamily: 'Inter, sans-serif' }}>

      {/* Top bar: logo + stepper + skip */}
      <div className="flex-shrink-0" style={{ background: '#fff', borderBottom: '1px solid #E5E5E8' }}>
        {/* Logo row */}
        <div className="flex items-center justify-between px-6 py-3" style={{ borderBottom: '1px solid #F0F0F2' }}>
          <div className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', color: 'var(--text)' }}
            />
            <span className="font-bold text-sm" style={{ color: '#1A1A1A' }}>Helios</span>
            <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ background: '#F5F5F7', color: '#8B8B94', marginLeft: '4px' }}>
              Налаштування
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
              >
                {user.avatar}
              </div>
              <span className="text-sm font-medium" style={{ color: '#1A1A1A' }}>{user.displayName}</span>
            </div>
            <button
              type="button"
              onClick={handleSkip}
              className="text-xs cursor-pointer transition-opacity whitespace-nowrap"
              style={{ color: '#AFAFB8' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#6B6B74'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#AFAFB8'; }}
            >
              Пропустити налаштування →
            </button>
          </div>
        </div>

        {/* Stepper */}
        <WizardStepper currentStep={step} steps={STEPS} />
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-8 max-w-6xl mx-auto w-full">
          {step === 1 && (
            <StepProfile
              data={data.profile}
              user={user}
              onChange={(profile) => setData((d) => ({ ...d, profile }))}
            />
          )}
          {step === 2 && (
            <StepCompany
              data={data.company}
              onChange={(company) => setData((d) => ({ ...d, company }))}
            />
          )}
          {step === 3 && (
            <StepModules
              selected={data.selectedModules}
              onChange={(selectedModules) => setData((d) => ({ ...d, selectedModules }))}
              userRole={user.role}
            />
          )}
          {step === 4 && (
            <StepIntegrations
              selected={data.selectedIntegrations}
              onChange={(selectedIntegrations) => setData((d) => ({ ...d, selectedIntegrations }))}
            />
          )}
          {step === 5 && (
            <StepTeam
              team={data.team}
              onChange={(team) => setData((d) => ({ ...d, team }))}
            />
          )}
        </div>
      </div>

      {/* Bottom navigation bar */}
      <div
        className="flex-shrink-0 flex items-center justify-between px-6 py-4"
        style={{ background: '#fff', borderTop: '1px solid #E5E5E8' }}
      >
        {/* Left: progress & price for modules step */}
        <div>
          {step === 3 ? (
            <div>
              <p className="text-xs" style={{ color: '#8B8B94' }}>
                Обрано: <strong style={{ color: '#1A1A1A' }}>{selectedModulesCount} модулів</strong>
              </p>
            </div>
          ) : (
            <p className="text-xs" style={{ color: '#AFAFB8' }}>
              Крок {step} з {STEPS.length}
            </p>
          )}
        </div>

        {/* Right: buttons */}
        <div className="flex items-center gap-3">
          {step > 1 && (
            <button
              type="button"
              onClick={goBack}
              className="px-5 py-2.5 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
              style={{ background: '#fff', border: '1.5px solid #E5E5E8', color: '#1A1A1A' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F5F5F7'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
            >
              ← Назад
            </button>
          )}
          <button
            type="button"
            onClick={goNext}
            className="px-6 py-2.5 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
            style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#B81E24'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--accent)'; }}
          >
            {step === 5 ? 'Завершити налаштування ✓' : 'Продовжити →'}
          </button>
        </div>
      </div>
    </div>
  );
}
